import React from 'react';

import InnerCircle from './InnerCircle';
import { Container, Path } from './styles';
import DonutInterface from './types';
import {
  coords,
  gapAngleOffsetInner,
  gapAngleOffsetOuter,
  minimumValue,
  radiusInner,
  radiusOuter,
  size
} from './utils';

const Donut: React.FC<DonutInterface> = ({ series, month, total }) => {
  const makeSegment = (
    { paths, subtotal }: { paths: JSX.Element[]; subtotal: number },
    {
      percent,
      color,
      icon,
      label
    }: {
      percent: number;
      color: string;
      icon: React.FC<{ fontSize: number; color: string }>;
      label: string;
    },
    i: number
  ) => {
    const startAngle = subtotal * 360 + 90; // +90 so we start at 12 o'clock
    const endAngle = startAngle + percent * 360;

    const angleOpening = endAngle - startAngle;

    const totalInNumber = Number(total?.replace(/[^0-9.-]+/g, '')) || total;

    // no gaps for values beneath the minimum threshold
    const useGap = percent >= minimumValue;
    const innerGap = useGap && totalInNumber > 0 ? gapAngleOffsetInner : 0;
    const outerGap = useGap && totalInNumber > 0 ? gapAngleOffsetOuter : 0;

    const startAngleInner = startAngle + innerGap;
    const startAngleOuter = startAngle + outerGap;
    const endAngleInner = endAngle - innerGap;
    const endAngleOuter = endAngle - outerGap;

    const round = 1;

    const [x1, y1] = coords(startAngleInner, radiusInner); // start point on inner circle
    const [x2, y2] = coords(startAngleOuter, radiusOuter - round); // start point on outer circle (before round)
    const [x3, y3] = coords(startAngleOuter + round, radiusOuter); // start point on outer circle (after round)
    const [x4, y4] = coords(endAngleOuter - round, radiusOuter); // end point on outer circle
    const [x5, y5] = coords(endAngleOuter, radiusOuter - round); // end point on outer circle
    const [x6, y6] = coords(endAngleInner, radiusInner); // end point on inner circle
    const [x7, y7] = coords(
      (startAngleInner + endAngleInner) / 2,
      (radiusInner + radiusOuter) / 2
    ); // center point of the segment

    const largeArc = percent > 0.5 ? 1 : 0;
    const sweepOuter = 1;
    const sweepInner = 0;

    const commands = [
      // move to start angle coordinate, inner radius
      `M${x1} ${y1}`,

      // line to start angle coordinate, outer radius
      `L${x2} ${y2}`,

      `A${0} ${0} 0 ${0} ${1} ${x3} ${y3}`,

      // arc to end angle coordinate, outer radius
      `A${radiusOuter} ${radiusOuter} 0 ${largeArc} ${sweepOuter} ${x4} ${y4}`,

      `A${round} ${round} 0 ${0} ${1} ${x5} ${y5}`,

      // line to end angle coordinate, inner radius
      `L${x6} ${y6}`,

      // arc back to start angle coordinate, inner radius
      `A${radiusInner} ${radiusInner} 0 ${largeArc} ${sweepInner} ${x1} ${y1}`
    ];

    const fillProp = { fill: color };

    const offsetCenter = 5;
    const translateX = x7 - offsetCenter;
    const translateY = y7 - offsetCenter;

    const Icon = icon && angleOpening > 25 ? icon : null;

    paths.push(
      <React.Fragment key={i}>
        <Path
          data-testid="segment"
          data-category={label}
          data-percent={percent}
          key={i}
          {...fillProp}
          stroke="none"
          d={commands.join(' ')}
        />
        <g
          data-testid="segment-icon"
          style={{ position: 'relative' }}
          transform={`translate(${translateX}, ${translateY})`}
          transform-origin="center"
        >
          {Icon && <Icon color="#fff" fontSize={10} />}
        </g>
      </React.Fragment>
    );

    return {
      paths,
      subtotal: subtotal + percent
    };
  };

  const computePercentages = () => {
    // eliminate values of zero or less; protects against division by zero when computing percentages
    const filtered = (series || []).filter(({ value }) => value > 0);
    const total = filtered.reduce((t, { value = 0 }) => t + value, 0);

    const percents = filtered.map((item) => ({
      ...item,
      percent: item.value / total
    }));

    return percents;
  };

  const items = computePercentages();

  return !(items || []).length ? null : (
    <Container>
      <svg viewBox={`0 0 ${size} ${size}`} data-testid="donut">
        {items.reduce(makeSegment, { paths: [], subtotal: 0 }).paths}
        <InnerCircle
          data-testid="donut-inner"
          month={month}
          size={size}
          radius={radiusInner}
          total={total}
        />
      </svg>
    </Container>
  );
};

export default Donut;
